"use client";

import type { ComponentType, SVGProps } from "react";
import {
  BanknotesIcon,
  CheckBadgeIcon,
  ClipboardDocumentListIcon,
  EyeIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { HOMEPAGE_COPY } from "@/lib/homepage/copy";

type StepIconId =
  | "list"
  | "reviewed"
  | "meet"
  | "paid"
  | "browse"
  | "pay"
  | "inspect"
  | "confirm";

type Step = {
  title: string;
  description: string;
  icon: StepIconId;
};

const STEP_ICONS: Record<StepIconId, ComponentType<SVGProps<SVGSVGElement>>> = {
  list: ClipboardDocumentListIcon,
  reviewed: ShieldCheckIcon,
  meet: UserGroupIcon,
  paid: BanknotesIcon,
  browse: MagnifyingGlassIcon,
  pay: LockClosedIcon,
  inspect: EyeIcon,
  confirm: CheckBadgeIcon,
};

function StepScroller({
  steps,
  cardClassName,
  iconClassName,
}: {
  steps: readonly Step[];
  cardClassName: string;
  iconClassName: string;
}) {
  return (
    <div className="-mx-4 px-4 md:mx-0 md:px-0">
      <div
        className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth [scrollbar-width:thin]"
        role="list"
      >
        {steps.map((step) => {
          const Icon = STEP_ICONS[step.icon];
          return (
            <div
              key={step.title}
              role="listitem"
              className={`flex flex-col items-center text-center p-6 rounded-xl shrink-0 w-[min(80vw,17.5rem)] snap-start ${cardClassName}`}
            >
              <div
                className={`mb-4 flex h-12 w-12 items-center justify-center rounded-lg ${iconClassName}`}
                aria-hidden
              >
                <Icon className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
              <p className="text-stone-600 text-sm md:text-base">
                {step.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function HomepageEducationSection() {
  const { heading, intro, sellersLabel, buyersLabel, sellerSteps, buyerSteps } =
    HOMEPAGE_COPY.education;

  return (
    <>
      <section className="w-full max-w-7xl mb-12" id="how-it-works">
        <h2 className="text-3xl md:text-4xl font-semibold text-center mb-3">
          {heading}
        </h2>
        <p className="text-base md:text-lg text-text-light text-center max-w-2xl mx-auto mb-8 leading-relaxed">
          {intro}
        </p>
        <p className="text-2xl md:text-xl font-semibold text-primary text-center mb-3">
          {sellersLabel}
        </p>
        <div className="mt-6">
          <StepScroller
            steps={sellerSteps}
            cardClassName="bg-primary-100 text-stone-900"
            iconClassName="bg-white/70 text-primary"
          />
        </div>
      </section>

      <section className="w-full max-w-7xl mb-12" id="how-it-works-buyers">
        <p className="text-2xl md:text-xl font-semibold text-yellow-500 text-center mb-3">
          {buyersLabel}
        </p>
        <div className="mt-6">
          <StepScroller
            steps={buyerSteps}
            cardClassName="bg-yellow-100 text-stone-900"
            iconClassName="bg-white/70 text-amber-700"
          />
        </div>
      </section>
    </>
  );
}
